<script>
  import router from '../../../lib/router';
  import { onMount } from 'svelte';
  import { fade } from 'svelte/transition';
  import moment from 'moment';
  import { show_message } from '../../../lib/flash';
  import { t9n } from '../../../lib/translate.js';
  import { TS } from '../../../config/translation-keys.js';
  import { NotificationError } from '../../../lib/errors.js';
  import Dialog from '../../../lib/components/Dialog.svelte';
  import APIClient from '../../../state-charts/api-client';
  import session from '../../../lib/session';
  import settings from '../../../config';
  import Events from '../../../lib/events';
  import { check_ws_connection, connect } from '../../../lib/websocket';
  import { chat_count } from '../../../chat/unread_chats_count.js';

  // internal variables ------------------------------------------------------ //

  let notifications = [];
  let unread_count = 0;
  let show_notifications = false;
  let current_page = 0;
  let per_page = 50;
  let notifications_count = 0;
  let no_notification_label = t9n(TS.NOTIFICATION_MESSAGE_EMPTY);
  let order_not_available_anymore = t9n(TS.NOTIFICATIONS_TASK_NOT_AVAILABLE);
  let listed_notifications = [];
  let merchant_logos = [];

  const ORDERER_NOTIFICATION_TYPES = [
    'push_new_applicant',
    'push_cancelled_by_worker',
    'push_marked_completed',
    'push_reminder_from_worker',
  ];
  const WORKER_NOTIFICATION_TYPES = [
    'push_got_task',
    'push_new_created',
    'push_cancelled_by_orderer',
    'push_approved',
    'push_not_approved',
  ];
  const ORDER_STATE_NOTIFICATION_TYPES =
    ORDERER_NOTIFICATION_TYPES + WORKER_NOTIFICATION_TYPES;
  const CHAT_MESSAGE_NOTIFICATION_TYPE = 'push_new_chat_message';
  const CONFIG_CHANGE_NOTIFICATION_TYPE = 'config_changed';
  const TOU_CHANGE_NOTIFICATION_TYPE = 'push_tou_change';
  const DOCUMENT_NOTIFICATION_TYPES = [
    'push_doc_verified',
    'push_doc_rejected',
  ];
  const ORDER_STATES = {
    INITIALIZED: 'order_status_initialized',
    IN_PROGRESS: 'order_status_in_progress',
    DELIVERED: 'order_status_delivered',
    NOT_APPROVED: 'order_status_not_approved',
    COMPLETED: 'order_status_completed',
    CANCELLED_BY_WORKER: 'order_status_cancelled_by_worker',
    CANCELLED_BY_ORDERER: 'order_status_cancelled_by_orderer',
    CANCELLED_BY_ADMIN: 'order_status_cancelled_by_admin',
  };

  let noti_label;
  let release_notes_text;
  let noti_text;
  let btn_close;
  let switch_dialog;

  const finnish_release_notes_link =
    'https://workpilots.zendesk.com/hc/fi/articles/207401309-Ajankohtaista';
  const english_release_notes_link =
    'https://workpilots.zendesk.com/hc/en-us/articles/207401309-Important-news';

  // utility functions -------------------------------------------------------- //

  const close_notifications = () => {
    document.getElementById('noti-button').click();
  };

  const toggle_show_notifications = () => {
    let main_content = document.querySelector('#main-content');

    show_notifications = !show_notifications;
    if (show_notifications === true) {
      main_content.classList.add('blur');
      main_content.addEventListener('click', close_notifications);
    } else {
      main_content.removeEventListener('click', close_notifications);
      main_content.classList.remove('blur');
    }
  };

  const translate_texts = () => {
    noti_label = t9n(TS.NOTIFICATION_DIFFERENT_SERVICE_LABEL);
    btn_close = t9n(TS.LOGIN_FORM_CLOSE);
    release_notes_text = t9n(TS.NOTIFICATIONS_RELEASE_NOTES_TEXT);
  };

  const switch_merchant = async (id, url) => {
    await APIClient.switch_to_merchant({ merchant_uuid: id }, url);
  };

  const create_invite_dialog = async (token, partner, merchant_uuid) => {
    router.navigate(
      '/switch-service' +
        `?token=${token}&merchant=${merchant_uuid}&partner=${partner}`
    );
  };

  const create_switch_dialog = (merchant_uuid, url) => {
    let target = document.querySelector('.switch-dialog');

    target.innerHTML = '';

    let target_name = get_merchant_name(merchant_uuid);
    let content_text = t9n(TS.NOTIFICATIONS_CHANGE_SERVICE, target_name);
    switch_dialog = new Dialog({
      target: target,
      props: {
        id: 'noti-message',
        title: noti_label,
        content: content_text,
        buttons: [
          {
            label: t9n(TS.PILOT_POINTS_CANCEL),
            action: () => {
              switch_dialog.close();
              switch_dialog = null;
            },
            classes: [],
          },
          {
            label: t9n(TS.TXT_YES),
            action: async () => {
              switch_dialog.close();
              switch_dialog = null;
              await switch_merchant(merchant_uuid, url);
            },
            classes: [],
          },
        ],
      },
    });

    return switch_dialog;
  };

  const open_release_notes = () => {
    if ($session.language === 'fi') {
      window.open(finnish_release_notes_link);
    } else {
      window.open(english_release_notes_link);
    }
  };

  const open_not_available_anymore_warning = () => {
    show_message(order_not_available_anymore, 'info');
  };

  const get_merchant_name = id => {
    let merchant = $session.user.registered_merchants.find(m => m.uuid === id);
    return !!merchant ? merchant.name : 'A Private Service';
  };

  const handle_notification_click = (url, merchant_uuid, invite, partner) => {
    if (!!merchant_uuid) {
      let user_merchant = $session.user.merchant_uuid;
      if (user_merchant === merchant_uuid) {
        if (url === 'release') {
          open_release_notes();
        } else if (url === 'not_available') {
          open_not_available_anymore_warning();
        } else if (!!url) router.navigate(url);
      } else if (invite) {
        create_invite_dialog(invite, partner, merchant_uuid);
      } else {
        create_switch_dialog(merchant_uuid, url);
        switch_dialog.open();
      }
    }
    toggle_show_notifications();
  };

  const handle_notification_hover = async e => {
    let nid = e.target.dataset.notification_id;
    let notification = notifications.find(
      n => n.notification_id.toString() === nid
    );

    if (!!nid && !!notification && notification.read === false) {
      await APIClient.read_notifications({ notification_ids: [nid] }, data => {
        notification.read = true;
        notifications = [...notifications];
        unread_count -= unread_count === 0 ? 0 : 1;
      });
    }
  };

  function get_logo(notification, logo) {
    if (!!notification.merchant) {
      if (!!notification.merchant.uuid) {
        let contains = merchant_logos.find(
          e => e.merchant_uuid === notification.merchant.uuid
        );

        if (!!contains) {
          logo = contains.logo_url;
        }
      }
    }
    return logo;
  }

  const notification_icon = notification => {
    let image;
    let merchant_logo = false;
    let logo = '';
    logo = get_logo(notification, logo);

    // Special cases:
    if (
      notification.resource_id === 'push_new_chat_message' &&
      notification.data.hasOwnProperty('partner')
    ) {
      return notification.data.partner.image_url;
    }

    if (notification.resource_id === 'push_invited_to_merchant') {
      image = 'person_add.svg';
      return `/assets/notifications/${image}`;
    }

    if (notification.resource_id === 'push_release_notes') {
      image = 'megaphone.svg';
      return `/assets/notifications/${image}`;
    }

    // Order related:
    if (notification.resource_id === 'push_new_applicant') {
      image = 'doc_person_employer.svg';
    } else if (notification.resource_id === 'push_new_created') {
      image = 'suitable_job_posted.svg';
    } else if (notification.resource_id === 'push_approved') {
      image = 'doc_ok_worker.svg';
    } else if (notification.resource_id === 'push_got_task') {
      image = 'doc_person_worker.svg';
    } else if (notification.resource_id === 'push_marked_completed') {
      image = 'doc_ok_employer.svg';
    } else if (notification.resource_id === 'push_not_approved') {
      image = 'not_approved.svg';
    } else if (notification.resource_id === 'push_reminder_from_worker') {
      image = 'doc_ok_employer.svg';
    } else {
      if (logo.length > 0) {
        image = logo;
        merchant_logo = true;
      } else {
        image = 'ic_logo.svg'; // main WP logo
      }
    }

    if (merchant_logo === true) {
      return image;
    } else {
      return `/assets/notifications/${image}`;
    }
  };

  const get_notification_url = notification => {
    let url_role;
    let is_chat_message =
      notification.resource_id === CHAT_MESSAGE_NOTIFICATION_TYPE;
    if (ORDERER_NOTIFICATION_TYPES.includes(notification.resource_id)) {
      url_role = 'orderer';
    } else if (WORKER_NOTIFICATION_TYPES.includes(notification.resource_id)) {
      url_role = 'worker';
    } else if (is_chat_message) {
      return `/chats/${notification.data.order.order_uuid}/partner/${notification.partner.id}`;
    }

    if ($session.user && $session.user.last_role !== url_role) return null;

    return `/order-details?id=${notification.data.order.order_uuid}`;
  };

  /**
   * status only has the value if the user has the right to see it, otherwise it's empty
   * which means: if it's initialized state or if the user is the worker or the orderer
   */
  function set_title(notification) {
    if (notification.resource_id === 'push_new_created') {
      if (notification.data.order.status !== '') {
        switch (notification.data.order.status) {
          case ORDER_STATES.INITIALIZED:
            return t9n('push_new_created');
          case ORDER_STATES.IN_PROGRESS:
            return t9n('push_new_created');
          case ORDER_STATES.COMPLETED:
            notification.not_available = 'not_available';
            return t9n('push_new_created');
          case ORDER_STATES.DELIVERED:
            return t9n('push_new_created');
          case ORDER_STATES.NOT_APPROVED:
            return t9n('push_new_created');
          case ORDER_STATES.CANCELLED_BY_WORKER:
            return t9n('push_new_created');
          case ORDER_STATES.CANCELLED_BY_ORDERER:
            notification.not_available = 'not_available';
            return t9n('order_status_cancelled_by_orderer');
          case ORDER_STATES.CANCELLED_BY_ADMIN:
            notification.not_available = 'not_available';
            return t9n('order_status_cancelled_by_admin');
        }
      } else {
        notification.not_available = 'not_available';
        // if (settings.env === 'development') console.log('Empty status', { notification });
        return t9n('push_new_created');
      }
    } else {
      return t9n(notification.resource_id);
    }
  }

  function set_partner(notification, partner) {
    if (notification.hasOwnProperty('partner')) {
      if (
        notification.partner.hasOwnProperty('organization_name') &&
        !!notification.partner.organization_name
      ) {
        partner = notification.partner.organization_name;
      } else {
        partner = `${notification.partner.first_name} ${notification.partner.last_name}`;
      }
    }
    return partner;
  }

  function set_merchant_uuid(notification) {
    if (!!notification.merchant) return notification.merchant.uuid;
  }

  function set_merchant_name(notification) {
    if (!!notification.merchant) return notification.merchant.name;
  }

  const parse_notification = notification => {
    const id = notification.notification_id;
    const read = notification.read;
    const created_at = moment.utc(notification.created_at).local();
    const created_at_label = moment(created_at).fromNow();
    const img_url = notification_icon(notification);

    let url;
    let invite;
    let text;
    let merchant_uuid = set_merchant_uuid(notification);
    let merchant_name = set_merchant_name(notification);
    let title = set_title(notification);
    let partner = set_partner(notification);

    // ORDERS, CARDS:
    if ('data' in notification) {
      if ('order' in notification.data) {
        text = notification.data.order.task_name;
        url = get_notification_url(notification);
        if (!url) {
          // if (settings.env === 'development') console.log('NO URL!', notification);
          return null;
        }
      } else if ('text' in notification.data) {
        text = notification.data.text;
      } else if ('card' in notification.data) {
        text = t9n(
          notification.resource_id,
          notification.data.card.masked_number
        );
        title = text;
        url = '/cards';
        merchant_uuid = $session.user.merchant_uuid;
      }
    } else {
      text = '';
    }

    // SPECIAL CASES:
    if (notification.not_available === 'not_available') {
      url = 'not_available';
    }

    if (notification.resource_id === 'push_invited_to_merchant') {
      invite = notification.data.text;
      text = merchant_name;
      title = t9n(TS.NOTIFICATIONS_INVITATION_TO_SERVICE);
    }

    if (notification.resource_id === 'push_release_notes') {
      url = 'release';
      title = release_notes_text;
      merchant_uuid = $session.user.merchant_uuid;
    }

    return {
      merchant_uuid,
      id,
      title,
      text,
      partner,
      created_at_label,
      url,
      img_url,
      read,
      invite,
    };
  };

  const sort_by_creation_date = (n1, n2) => {
    const t1 = n1.created_at;
    const t2 = n2.created_at;

    return t1 === t2 ? 0 : t1 > t2 ? -1 : 1;
  };

  const parse_notifications = notifications => {
    if (!!notifications && notifications.length > 0) {
      let sorted = [...notifications].sort(sort_by_creation_date);
      let parsed = sorted.map(parse_notification);
      listed_notifications = parsed.filter(n => !!n);
      notifications_count = listed_notifications.length;
      unread_count = listed_notifications.filter(n => !n.read).length;
    }
  };

  const get_merchant_logos = async notifications => {
    let array = [];
    for (let i = 0; i < notifications.length; i++) {
      if (!!notifications[i].merchant) {
        if (!!notifications[i].merchant.uuid) {
          let contains = array.find(
            e => e.merchant_uuid === notifications[i].merchant.uuid
          );
          if (contains) {
            // console.log("NOTI ALREADY CONTAINS", i, notifications[i].merchant.uuid)
          } else {
            if (notifications[i].merchant.uuid !== $session.merchant_uuid) {
              let temp_logo = '/assets/notifications/ic_logo.svg';

              if (!!notifications[i]) {
                if (!!notifications[i].merchant) {
                  if (!!notifications[i].merchant.logo_url)
                    temp_logo = notifications[i].merchant.logo_url;
                }
              }

              let json = {
                merchant_uuid: notifications[i].merchant.uuid,
                logo_url: temp_logo,
              };
              array.push(json);
            }
          }
        } else {
          console.log('No merchant uuid!', i);
        }
      }
    }
    merchant_logos = array;
  };

  $: get_merchant_logos(notifications);
  $: if (!!merchant_logos) {
    parse_notifications(notifications);
  }

  $: {
    $session.language;
    parse_notifications(notifications);
    translate_texts();
  }

  const check_for_double_noti = notification => {
    for (let i = 0; i < notifications.length; i++) {
      if (
        notifications[i].notification_id === notification.detail.notification_id
      ) {
        // console.log(
        //   'Found double notification: ',
        //   notification.detail.notification_id,
        //   notifications[i].notification_id,
        //   i
        // );
        return;
      }
    }
    handle_received_notification(notification);
  };

  const handle_received_notification = notification => {
    notifications = Array.from(
      new Set([...notifications, notification.detail])
    );
    unread_count += 1;
    parse_notifications(notifications);
    if (notification.detail.resource_id === CHAT_MESSAGE_NOTIFICATION_TYPE)
      chat_count.update(n => n + 1);
  };

  const load_notifications = async () => {
    await APIClient.get_notifications(
      { page: current_page + 1, number_of_items_per_page: per_page },
      data => {
        current_page = data.results_info.current_page;
        notifications_count = data.results_info.number_of_items;
        notifications = [...notifications, ...data.notifications];
        unread_count = data.unread;
      }
    );

    if (notifications.length < notifications_count) {
      await load_notifications();
    }
  };

  export const init_notifications = async () => {
    if (!$session.token || !$session.user || !$session.merchant) {
      return;
    }

    translate_texts();
    await load_notifications();

    window.addEventListener(
      'wp:ws:handle_received_notification',
      check_for_double_noti
    );

    try {
      connect();
      setInterval(check_ws_connection, 5 * 1000);
    } catch (e) {
      console.debug(e);
      return false;
    }
  };

  onMount(async () => {
    await init_notifications();
  });
</script>

<button
  id="noti-button"
  class="material-icons mdc-top-app-bar__navigation-icon mdc-icon-button
  brand-color"
  on:click={toggle_show_notifications}>
  notifications_active
</button>

<span class="unread-count-badge" class:hidden-with-opacity={!unread_count}>
  {unread_count}
</span>

<span class="arrow" class:hidden-with-opacity={!show_notifications} />

<ul
  id="notification-wrap"
  class="mdc-list mdc-list--two-line mdc-list--avatar-list notification-list
  fade-in"
  class:hidden={!show_notifications}>
  {#if !!listed_notifications.length && !!notifications_count}
    {#each listed_notifications as n}
      <li
        class="mdc-list-item mdc-ripple-upgraded"
        tabindex="0"
        id="e2344047-7d25-49ea-b584-17ea4d520023"
        style="--mdc-ripple-fg-size:360px; --mdc-ripple-fg-scale:1.7064;
        --mdc-ripple-fg-translate-start:-85.6445px, -155.127px;
        --mdc-ripple-fg-translate-end:120px, -144.004px;"
        data-notification_id={n.id}
        data-target_url={n.url}
        on:click={() => handle_notification_click(n.url, n.merchant_uuid, n.invite, n.partner)}
        on:mouseenter={async e => {
          await handle_notification_hover(e);
        }}>
        <span class="mdc-list-item__graphic material-icons" aria-hidden="true">
          <span class="dot" class:unread={!n.read} />
          {#if n.img_url}
            <img src={n.img_url} width="20px" alt="avatar" />
          {:else}folder{/if}
        </span>

        <span class="mdc-list-item__text">
          <span class="mdc-list-item__primary-text">{n.title}</span>
          <span class="mdc-list-item__secondary-text">{n.text}</span>
        </span>
      </li>
    {/each}
  {:else}
    <li class="no-items">{no_notification_label}</li>
  {/if}
</ul>

<div class="switch-dialog" />

<style>
  .overlay-class {
    position: fixed;
    top: 0;
    left: 0;
    z-index: 111;
    opacity: 0;
    width: 100%;
    height: 100%;
  }

  .brand-color {
    color: var(--orderer-foreground-color);
  }

  .unread-count-badge {
    display: block;
    position: relative;
    top: -11px;
    right: 22px;
    background-color: red;
    color: white;
    border-radius: 50%;

    width: 20px;
    height: 20px;
    font-size: 11px;
    font-weight: bold;
    text-align: center;
    padding: 0;
    overflow: hidden;
    line-height: 21px;
  }

  .notification-list {
    font-size: 0.85em;
    position: absolute;
    /*top: 45px;*/
    /*left: 140px;*/
    top: 50px;
    left: 95px;
    background-color: white;
    border-radius: 4px;
    max-height: 285px;
    /*height: 285px;*/
    max-width: 500px;
    width: 450px;
    min-height: 215px;
    overflow: auto;
    -moz-box-shadow: 0 0 4px rgba(0, 0, 0, 0.2);
    -webkit-box-shadow: 0 0 4px rgba(0, 0, 0, 0.2);
    box-shadow: 0 8px 10px 1px rgba(0, 0, 0, 0.14),
      0 3px 14px 2px rgba(0, 0, 0, 0.12), 0 5px 5px -3px rgba(0, 0, 0, 0.2);
  }

  .mdc-list li {
    border-bottom: 1px solid lightgrey;
  }

  .hidden {
    display: none;
  }

  .hidden-with-opacity {
    opacity: 0;
    transition: all 1s ease;
  }

  .dot {
    background-color: white;
    border-radius: 50%;
    position: relative;
    left: -7px;
    height: 8px;
    width: 8px;
  }

  .dot.unread {
    background-color: #006699;
  }

  .no-items {
    width: 100%;
    height: 170px;
    text-align: center;
    margin-top: 25%;
  }

  .arrow {
    content: ' ';
    position: relative;
    top: 15px;
    left: -11px;
    margin-left: -5px;
    border-width: 8px;
    border-style: solid;
    border-color: transparent transparent #f6f9f9 transparent;
  }

  /* Mobile Mode
    *******************************************************/
  @media (min-width: 0) and (max-width: 600px) {
    .notification-list {
      width: fit-content;
      /*position: relative;*/
      /* width: 50%; */
      /* height: 50%; */
      overflow: auto;
      position: absolute;
      left: 0;
      bottom: 0;
      right: 0;
      margin-left: auto;
      margin-right: auto;
    }

    .mdc-list-item__graphic {
      display: none;
    }

    .arrow {
      left: -25px;
    }
  }
</style>
